/**
 * Panel 5: Cross-Domain Pattern Comparison
 *
 * Enhanced N-way (2-3 asset) comparison with:
 *  - Weighted correlation scoring
 *  - Drill-down insights per finding
 *  - Fingerprint overlay bar chart
 *  - Fusion cluster linkage
 *  - Sorted asset dropdowns (domain then name)
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { AssetSummary, FusionCluster } from '../../../types'
import { getForceComposition, getFusionClusters, formatPercent } from '../../../services/api'

// ── Scoring weights ──────────────────────────────────────────────────
const W_MC = 0.30
const W_READINESS = 0.25
const W_THREAT = 0.20
const W_DOMAIN = 0.15
const W_TEMPO = 0.10

// ── Fingerprint axis config ──────────────────────────────────────────
const FP_AXES: { key: keyof NonNullable<AssetSummary['fingerprint']>; label: string; color: string }[] = [
  { key: 'readiness', label: 'Readiness', color: '#3b82f6' },
  { key: 'platform', label: 'Platform', color: '#8b5cf6' },
  { key: 'logistics', label: 'Logistics', color: '#f59e0b' },
  { key: 'mission', label: 'Mission', color: '#22c55e' },
]

// ── Asset slot colors ────────────────────────────────────────────────
const SLOT_COLORS = ['#6366f1', '#f59e0b', '#22d3ee'] as const
const SLOT_LABELS = ['A', 'B', 'C'] as const

// =====================================================================
//  Correlation helpers
// =====================================================================

interface PairScore {
  a: AssetSummary
  b: AssetSummary
  labelA: string
  labelB: string
  total: number
  mcComponent: number
  readinessComponent: number
  threatComponent: number
  domainComponent: number
  tempoComponent: number
  insights: string[]
}

function computePairScore(a: AssetSummary, b: AssetSummary, labelA: string, labelB: string): PairScore {
  // MC rate proximity: 1 - |delta| (both values are 0-1 fractions)
  const mcDelta = Math.abs(a.mc_rate - b.mc_rate)
  const mcProximity = Math.max(0, 1 - mcDelta)
  const mcComponent = mcProximity * W_MC * 100

  // Readiness proximity: 1 - |delta|/100 (scores are 0-100)
  const readDelta = Math.abs(a.readiness_score - b.readiness_score)
  const readProximity = Math.max(0, 1 - readDelta / 100)
  const readinessComponent = readProximity * W_READINESS * 100

  // Threat similarity: 1 - |delta| (both 0-1)
  const threatDelta = Math.abs(a.threat_exposure - b.threat_exposure)
  const threatProximity = Math.max(0, 1 - threatDelta)
  const threatComponent = threatProximity * W_THREAT * 100

  // Same domain bonus
  const sameDomain = a.domain === b.domain
  const domainComponent = sameDomain ? W_DOMAIN * 100 : 0

  // Same tempo bonus
  const sameTempo = a.current_tempo === b.current_tempo
  const tempoComponent = sameTempo ? W_TEMPO * 100 : 0

  const total = mcComponent + readinessComponent + threatComponent + domainComponent + tempoComponent

  // Build contextual insights
  const insights: string[] = []

  const mcDeltaPP = (mcDelta * 100).toFixed(0)
  if (mcDelta > 0.20) {
    insights.push(`MC rate delta of ${mcDeltaPP}pp suggests different sustainment priorities`)
  } else if (mcDelta < 0.05) {
    insights.push(`MC rates within 5pp — similar sustainment posture`)
  }

  if (readDelta > 30) {
    insights.push(`Readiness gap of ${readDelta.toFixed(0)} pts — significant capability divergence`)
  } else if (readDelta < 10) {
    insights.push(`Readiness within 10 pts — comparable operational capability`)
  }

  if (threatDelta < 0.05) {
    insights.push(`Threat convergence at ${threatDelta.toFixed(3)} — likely in same AOR`)
  } else if (threatDelta > 0.3) {
    insights.push(`Threat divergence of ${threatDelta.toFixed(2)} — operating in different threat environments`)
  }

  if (sameTempo) {
    insights.push(`Both operating in ${formatTempoLabel(a.current_tempo)} tempo — shared operational demand`)
  } else {
    insights.push(`Tempo mismatch: ${formatTempoLabel(a.current_tempo)} vs ${formatTempoLabel(b.current_tempo)}`)
  }

  if (sameDomain) {
    insights.push(`Same domain (${a.domain}) — direct force-structure comparison valid`)
  } else {
    insights.push(`Cross-domain (${a.domain}/${b.domain}) — joint integration lens applies`)
  }

  return { a, b, labelA, labelB, total, mcComponent, readinessComponent, threatComponent, domainComponent, tempoComponent, insights }
}

function formatTempoLabel(t: string): string {
  return t.replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase())
}

function scoreColor(score: number): string {
  if (score >= 80) return 'text-term-success'
  if (score >= 60) return 'text-term-warning'
  return 'text-term-error'
}

function scoreLabel(score: number): string {
  if (score >= 80) return 'CORRELATED'
  if (score >= 60) return 'PARTIAL'
  return 'DIVERGENT'
}

// =====================================================================
//  Main Component
// =====================================================================

export function CrossDomainPanel() {
  const [assets, setAssets] = useState<AssetSummary[]>([])
  const [clusters, setClusters] = useState<FusionCluster[]>([])
  const [loading, setLoading] = useState(true)
  const [slotA, setSlotA] = useState<string | null>(null)
  const [slotB, setSlotB] = useState<string | null>(null)
  const [slotC, setSlotC] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [forceRes, clusterRes] = await Promise.all([
        getForceComposition(),
        getFusionClusters(),
      ])
      if (forceRes.data) {
        const sorted = [...forceRes.data.assets].sort((a, b) => {
          const domCmp = a.domain.localeCompare(b.domain)
          if (domCmp !== 0) return domCmp
          return a.asset_type.localeCompare(b.asset_type)
        })
        setAssets(sorted)
        if (sorted.length >= 2) {
          setSlotA(sorted[0].id)
          setSlotB(sorted[1].id)
        }
      }
      if (clusterRes.data) {
        setClusters(clusterRes.data)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Resolved assets
  const assetA = useMemo(() => assets.find(a => a.id === slotA) ?? null, [assets, slotA])
  const assetB = useMemo(() => assets.find(a => a.id === slotB) ?? null, [assets, slotB])
  const assetC = useMemo(() => assets.find(a => a.id === slotC) ?? null, [assets, slotC])

  // Build all selected assets list
  const selected = useMemo(() => [assetA, assetB, assetC].filter(Boolean) as AssetSummary[], [assetA, assetB, assetC])

  // Pairwise scores
  const pairScores = useMemo(() => {
    const pairs: PairScore[] = []
    if (assetA && assetB) pairs.push(computePairScore(assetA, assetB, 'A', 'B'))
    if (assetA && assetC) pairs.push(computePairScore(assetA, assetC, 'A', 'C'))
    if (assetB && assetC) pairs.push(computePairScore(assetB, assetC, 'B', 'C'))
    return pairs
  }, [assetA, assetB, assetC])

  // Matching fusion clusters (any cluster containing 2+ of the selected assets)
  const matchingClusters = useMemo(() => {
    if (selected.length < 2) return []
    const selectedIds = new Set(selected.map(a => a.asset_type))
    return clusters.filter(c => {
      const overlap = c.assets.filter(a => selectedIds.has(a))
      return overlap.length >= 2
    }).map(c => {
      const overlap = c.assets.filter(a => selectedIds.has(a))
      return { cluster: c, overlappingAssets: overlap }
    })
  }, [selected, clusters])

  if (loading) return <div className="animate-pulse h-full bg-term-bgLight rounded" />

  return (
    <div className="flex flex-col gap-4 h-full overflow-auto">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
        Cross-Domain Pattern Comparison
      </div>

      {/* ── Selectors: 3 columns ──────────────────────── */}
      <div className="grid grid-cols-3 gap-3">
        <AssetDropdown
          label="A"
          color={SLOT_COLORS[0]}
          value={slotA}
          onChange={setSlotA}
          assets={assets}
          placeholder="Select asset A"
        />
        <AssetDropdown
          label="B"
          color={SLOT_COLORS[1]}
          value={slotB}
          onChange={setSlotB}
          assets={assets}
          placeholder="Select asset B"
        />
        <AssetDropdown
          label="C"
          color={SLOT_COLORS[2]}
          value={slotC}
          onChange={(v) => setSlotC(v || null)}
          assets={assets}
          placeholder="Add asset to compare"
          optional
        />
      </div>

      {/* ── Asset cards (2 or 3) ──────────────────────── */}
      {selected.length >= 2 && (
        <div className={`grid gap-3 ${selected.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {assetA && <AssetCard asset={assetA} slotLabel="A" slotColor={SLOT_COLORS[0]} />}
          {assetB && <AssetCard asset={assetB} slotLabel="B" slotColor={SLOT_COLORS[1]} />}
          {assetC && <AssetCard asset={assetC} slotLabel="C" slotColor={SLOT_COLORS[2]} />}
        </div>
      )}

      {/* ── Fingerprint overlay comparison ────────────── */}
      {selected.length >= 2 && (
        <FingerprintOverlay assets={selected} />
      )}

      {/* ── Pairwise Pattern Analysis ─────────────────── */}
      {pairScores.length > 0 && (
        <div className="space-y-3">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider">
            Pattern Analysis — {pairScores.length} pair{pairScores.length > 1 ? 's' : ''}
          </div>
          {pairScores.map((ps, i) => (
            <PairAnalysisCard key={i} pair={ps} />
          ))}
        </div>
      )}

      {/* ── Fusion Cluster linkage ────────────────────── */}
      {matchingClusters.length > 0 && (
        <div className="term-box p-3 border-term-primary/30">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">
            Fusion Cluster Linkage
          </div>
          <div className="space-y-2">
            {matchingClusters.map(({ cluster, overlappingAssets }) => (
              <div key={cluster.id} className="flex items-start gap-2 text-xs">
                <span className="text-term-primary font-bold shrink-0">{cluster.id}</span>
                <div>
                  <span className="text-term-text font-medium">{cluster.name}</span>
                  <span className="text-term-textDim ml-1">(r={cluster.correlation_strength.toFixed(2)})</span>
                  <div className="text-term-textDim mt-0.5">
                    Shared assets: {overlappingAssets.join(', ')}
                    {' '}&middot; Pattern: <span className="text-term-accent">{cluster.pattern_type}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected.length < 2 && (
        <div className="panel-placeholder flex-1">
          <div className="text-term-textDim text-sm">Select at least two assets to compare</div>
        </div>
      )}
    </div>
  )
}

// =====================================================================
//  Sub-components
// =====================================================================

function AssetDropdown({
  label,
  color,
  value,
  onChange,
  assets,
  placeholder,
  optional,
}: {
  label: string
  color: string
  value: string | null
  onChange: (v: string | null) => void
  assets: AssetSummary[]
  placeholder: string
  optional?: boolean
}) {
  // Group by domain
  const grouped = useMemo(() => {
    const m = new Map<string, AssetSummary[]>()
    for (const a of assets) {
      const arr = m.get(a.domain) || []
      arr.push(a)
      m.set(a.domain, arr)
    }
    return m
  }, [assets])

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className="w-4 h-4 rounded text-[10px] font-bold flex items-center justify-center text-term-bgDark"
          style={{ backgroundColor: color }}
        >
          {label}
        </span>
        <span className="text-[10px] text-term-textDim uppercase">{optional ? 'Optional' : 'Asset'}</span>
      </div>
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value || null)}
        className="term-input text-xs"
      >
        {optional && <option value="">{placeholder}</option>}
        {[...grouped.entries()].map(([domain, domAssets]) => (
          <optgroup key={domain} label={domain}>
            {domAssets.map(a => (
              <option key={a.id} value={a.id}>
                {a.asset_type} ({a.role})
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  )
}

function AssetCard({
  asset,
  slotLabel,
  slotColor,
}: {
  asset: AssetSummary
  slotLabel: string
  slotColor: string
}) {
  const fp = asset.fingerprint
  return (
    <div className="term-box p-3" style={{ borderTopColor: slotColor, borderTopWidth: '2px' }}>
      <div className="flex items-center gap-1.5 mb-1">
        <span
          className="w-4 h-4 rounded text-[10px] font-bold flex items-center justify-center text-term-bgDark shrink-0"
          style={{ backgroundColor: slotColor }}
        >
          {slotLabel}
        </span>
        <div className="text-sm font-bold text-term-text truncate">{asset.asset_type}</div>
      </div>
      <div className="text-[10px] text-term-textDim mb-2">{asset.role} | {asset.domain}</div>

      <div className="space-y-1.5 text-xs">
        <MetricRow label="MC Rate" value={formatPercent(asset.mc_rate)} />
        <MetricRow label="Readiness" value={`${asset.readiness_score.toFixed(0)}/100`} />
        <MetricRow label="Threat" value={asset.threat_exposure.toFixed(3)} />
        <MetricRow label="Tempo" value={formatTempoLabel(asset.current_tempo)} />
        <MetricRow label="Assigned" value={String(asset.assigned_count)} />
      </div>

      {/* Fingerprint with weight % */}
      {fp && (
        <div className="mt-2 space-y-1">
          <div className="text-[10px] text-term-textDim uppercase">Fingerprint</div>
          {FP_AXES.map(ax => (
            <MiniBar key={ax.key} label={ax.label} value={fp[ax.key]} color={ax.color} showPct />
          ))}
        </div>
      )}
    </div>
  )
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-term-textDim">{label}</span>
      <span className="text-term-text font-medium tabular-nums">{value}</span>
    </div>
  )
}

function MiniBar({
  label,
  value,
  color,
  showPct,
}: {
  label: string
  value: number
  color: string
  showPct?: boolean
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[9px] text-term-textDim w-14 truncate">{label}</span>
      <div className="flex-1 h-1.5 bg-term-bgDark rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <span className="text-[9px] text-term-textDim tabular-nums w-8 text-right">
        {showPct ? `${value.toFixed(0)}%` : value.toFixed(0)}
      </span>
    </div>
  )
}

// ── Fingerprint Overlay ──────────────────────────────────────────────

function FingerprintOverlay({ assets }: { assets: AssetSummary[] }) {
  const maxVal = 100 // fingerprint axes are 0-100
  return (
    <div className="term-box p-3">
      <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">
        Fingerprint Overlay
      </div>

      {/* Legend */}
      <div className="flex gap-4 mb-3">
        {assets.map((a, i) => (
          <div key={a.id} className="flex items-center gap-1.5 text-[10px]">
            <span
              className="w-3 h-3 rounded-sm shrink-0"
              style={{ backgroundColor: SLOT_COLORS[i] }}
            />
            <span className="text-term-textDim truncate max-w-[120px]">{a.asset_type}</span>
          </div>
        ))}
      </div>

      {/* Bar chart comparison */}
      <div className="space-y-3">
        {FP_AXES.map(ax => {
          const values = assets.map(a => a.fingerprint?.[ax.key] ?? 0)
          return (
            <div key={ax.key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-term-textDim font-medium">{ax.label}</span>
              </div>
              <div className="space-y-0.5">
                {assets.map((a, i) => {
                  const v = values[i]
                  return (
                    <div key={a.id} className="flex items-center gap-1.5">
                      <span
                        className="text-[8px] font-bold w-3 text-center shrink-0"
                        style={{ color: SLOT_COLORS[i] }}
                      >
                        {SLOT_LABELS[i]}
                      </span>
                      <div className="flex-1 h-2 bg-term-bgDark rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${(v / maxVal) * 100}%`,
                            backgroundColor: SLOT_COLORS[i],
                            opacity: 0.85,
                          }}
                        />
                      </div>
                      <span className="text-[9px] tabular-nums w-7 text-right" style={{ color: SLOT_COLORS[i] }}>
                        {v.toFixed(0)}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ── Pair Analysis Card ───────────────────────────────────────────────

function PairAnalysisCard({ pair }: { pair: PairScore }) {
  const [expanded, setExpanded] = useState(false)
  const roundedTotal = Math.round(pair.total)

  return (
    <div className="term-box p-3">
      {/* Header row */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-term-text">
            {pair.a.asset_type}
            <span className="text-term-textDim mx-1">&harr;</span>
            {pair.b.asset_type}
          </span>
          <span className="text-[10px] text-term-textDim">({pair.labelA}&harr;{pair.labelB})</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold tabular-nums ${scoreColor(roundedTotal)}`}>
            {roundedTotal}%
          </span>
          <span className={`term-badge text-[10px] ${
            roundedTotal >= 80 ? 'term-badge-success' :
            roundedTotal >= 60 ? 'term-badge-warning' :
            'term-badge-error'
          }`}>
            {scoreLabel(roundedTotal)}
          </span>
          <span className="text-term-textDim text-xs">{expanded ? '\u25B4' : '\u25BE'}</span>
        </div>
      </button>

      {/* Score breakdown bar */}
      <div className="mt-2 h-2 w-full bg-term-bgDark rounded-full overflow-hidden flex">
        <div className="h-full" style={{ width: `${pair.mcComponent}%`, backgroundColor: '#3b82f6' }} title={`MC Rate: ${pair.mcComponent.toFixed(1)}%`} />
        <div className="h-full" style={{ width: `${pair.readinessComponent}%`, backgroundColor: '#8b5cf6' }} title={`Readiness: ${pair.readinessComponent.toFixed(1)}%`} />
        <div className="h-full" style={{ width: `${pair.threatComponent}%`, backgroundColor: '#f59e0b' }} title={`Threat: ${pair.threatComponent.toFixed(1)}%`} />
        <div className="h-full" style={{ width: `${pair.domainComponent}%`, backgroundColor: '#22c55e' }} title={`Domain: ${pair.domainComponent.toFixed(1)}%`} />
        <div className="h-full" style={{ width: `${pair.tempoComponent}%`, backgroundColor: '#06b6d4' }} title={`Tempo: ${pair.tempoComponent.toFixed(1)}%`} />
      </div>

      {/* Mini legend */}
      <div className="flex gap-3 mt-1.5 text-[9px] text-term-textDim flex-wrap">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#3b82f6' }} />MC {pair.mcComponent.toFixed(0)}/{(W_MC * 100).toFixed(0)}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#8b5cf6' }} />Read {pair.readinessComponent.toFixed(0)}/{(W_READINESS * 100).toFixed(0)}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#f59e0b' }} />Threat {pair.threatComponent.toFixed(0)}/{(W_THREAT * 100).toFixed(0)}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#22c55e' }} />Domain {pair.domainComponent.toFixed(0)}/{(W_DOMAIN * 100).toFixed(0)}</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm" style={{ backgroundColor: '#06b6d4' }} />Tempo {pair.tempoComponent.toFixed(0)}/{(W_TEMPO * 100).toFixed(0)}</span>
      </div>

      {/* Expanded drill-down */}
      {expanded && (
        <div className="mt-3 pt-2 border-t border-term-border/50 space-y-1.5">
          <div className="text-[10px] text-term-textDim uppercase tracking-wider mb-1">Drill-Down Insights</div>
          {pair.insights.map((insight, idx) => (
            <div key={idx} className="flex gap-1.5 text-xs">
              <span className="text-term-textDim shrink-0">&rsaquo;</span>
              <span className="text-term-text">{insight}</span>
            </div>
          ))}

          {/* Detailed component scores */}
          <div className="mt-2 pt-2 border-t border-term-border/30">
            <div className="text-[10px] text-term-textDim uppercase tracking-wider mb-1">Component Scores</div>
            <div className="grid grid-cols-5 gap-1 text-[10px]">
              <ComponentScore label="MC Rate" earned={pair.mcComponent} max={W_MC * 100} color="#3b82f6" />
              <ComponentScore label="Readiness" earned={pair.readinessComponent} max={W_READINESS * 100} color="#8b5cf6" />
              <ComponentScore label="Threat" earned={pair.threatComponent} max={W_THREAT * 100} color="#f59e0b" />
              <ComponentScore label="Domain" earned={pair.domainComponent} max={W_DOMAIN * 100} color="#22c55e" />
              <ComponentScore label="Tempo" earned={pair.tempoComponent} max={W_TEMPO * 100} color="#06b6d4" />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ComponentScore({
  label,
  earned,
  max,
  color,
}: {
  label: string
  earned: number
  max: number
  color: string
}) {
  const pct = max > 0 ? (earned / max) * 100 : 0
  return (
    <div className="text-center">
      <div className="text-[9px] text-term-textDim truncate">{label}</div>
      <div className="font-bold tabular-nums" style={{ color }}>{earned.toFixed(1)}</div>
      <div className="w-full h-1 bg-term-bgDark rounded-full mt-0.5 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <div className="text-[8px] text-term-textDim">/{max.toFixed(0)}</div>
    </div>
  )
}
